import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { Emblem } from "../components/Emblem";
import { Button, ErrorNote, Input } from "../components/ui";
import { useTheme } from "../theme/ThemeContext";

export default function Login() {
  const { login, user, loading } = useAuth();
  const { firmName } = useTheme();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!loading && user) {
      navigate(user.role === "lawyer" ? "/desk" : "/portal", { replace: true });
    }
  }, [user, loading, navigate]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(undefined);
    setBusy(true);
    try {
      const signedIn = await login(email, password);
      navigate(signedIn.role === "lawyer" ? "/desk" : "/portal", { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Sign in failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg px-4 py-10">
      <main className="w-full max-w-sm">
        <div className="mb-8 flex flex-col items-center text-center">
          <Emblem size={72} className="text-ink" />
          <h1 className="mt-4 font-heading text-3xl font-bold">{firmName}</h1>
          <p className="mt-1 text-xs font-medium uppercase tracking-[0.28em] text-muted">
            Client Portal
          </p>
          <p className="mt-4 font-heading text-sm italic text-muted">
            Fiat justitia, ruat caelum.
          </p>
        </div>

        <form className="card card-gold-top p-6" onSubmit={submit} noValidate>
          <h2 className="font-heading text-lg font-semibold">Sign in</h2>
          <p className="mb-4 mt-1 text-sm text-muted">
            Access is limited to clients and chambers staff.
          </p>

          <div className="space-y-4">
            <Input
              label="Email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
            />
            <Input
              label="Password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          {error && (
            <div className="mt-4">
              <ErrorNote>{error}</ErrorNote>
            </div>
          )}

          <Button
            type="submit"
            variant="primary"
            loading={busy}
            className="mt-6 w-full"
          >
            {busy ? "Verifying…" : "Sign in"}
          </Button>
        </form>

        <p className="mt-6 text-center text-xs text-muted">
          Granted access only. If you cannot sign in, please contact the chambers.
        </p>
      </main>
    </div>
  );
}
